import React, { useEffect, useMemo, useState } from "react"

import { Flex, Space, Spin } from "antd"
import cls from "classnames"
import { t } from "i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import {
  COMPARE_IDS,
  getCompareParams
} from "../../../../../Components/Filters/CompareSelect/presets"
import FAQModal from "../../../../../Components/Modal/FAQModal"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { getDates } from "../../../../../Utils/utils"
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle"
import MetricsRow from "../../../components/Monitoring/MetricsRow/MetricsRow"
import { HIGHER_IS_BETTER } from "../../../components/Monitoring/monitoringCopy"
import PermissionPlaceholder from "../../../components/PermissionPlaceholder"
import useHealthAccessRestricted from "../../../hooks/useHealthAccessRestricted"
import { getMcMainChart } from "../../../redux/health.thunks"
import InfoLine from "../../../VAMP/components/InfoLine/InfoLine"
import SectionHeader from "../../../VAMP/components/SectionHeader/SectionHeader"
import { useIsStripeConnected } from "../../hooks/useIsStripeConnected"
import ConnectStripePlaceholder from "../ConnectStripePlaceholder/ConnectStripePlaceholder"
import MCChart from "../MCChart/MCChart"
import { mcFAQ } from "./mcFAQ"

import "../../../VAMP/components/MainChartBlock/MainChartBlock.less"

const totalBlocksStyles = {
  chargeback: {
    title: "Chargeback Rate",
    titleCount: "Chargeback Count",
    color: "#225AEA"
  },
  fraud: {
    title: "Fraud Rate",
    titleCount: "Fraud Count",
    color: "#DF7009"
  },
  three_ds: {
    title: "3DS Usage",
    titleCount: "3DS Payment Count",
    color: "#35BD29"
  }
}

const MCChartBlock = ({ filters, business_account_id }) => {
  const dispatch = useDispatch()
  const { isShowDemoData } = useDemoDataToggle()
  const { mcMainChart, mcMainChartFetching } = useSelector((state) => state.health)
  const { isMobile } = useSelector((state) => state.nav)
  const [dataType, setDataType] = useState("rate")
  const [isFAQOpen, setIsFAQOpen] = useState(false)
  const isRestricted = useHealthAccessRestricted()
  const isStripeConnected = useIsStripeConnected()
  const isComparisonVisible = Boolean(
    filters?.compare?.mode && filters.compare.mode !== COMPARE_IDS.NONE
  )

  useEffect(() => {
    if (filters?.period && isStripeConnected) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare)
      dispatch(
        getMcMainChart({
          ...period_query,
          ...compare_query,
          business_account: business_account_id
        })
      )
    }
  }, [filters, business_account_id, isShowDemoData, isStripeConnected])

  const metricBlocks = useMemo(() => {
    const isRate = dataType === "rate"
    const style = (key) => totalBlocksStyles[key]
    const title = (key) => (isRate ? style(key).title : (style(key).titleCount ?? style(key).title))

    if (!isStripeConnected) {
      const blank = (key, extra = {}) => ({
        value: null,
        title: title(key),
        color: style(key).color,
        isDashed: style(key).isDashed,
        ...extra
      })

      return ["chargeback", "fraud", "three_ds"].map((key) => blank(key))
    }

    const blocks = mcMainChart?.blocks_data
    const thresholds = mcMainChart?.thresholds
    if (!blocks || !thresholds) return []

    const compBlocks = mcMainChart?.comparison?.blocks_data
    const compLabel = mcMainChart?.comparison_period?.label
    const isRegionResolved = thresholds.efm?.rate != null
    const metric = (key, value, comparisonValue, extra = {}) => ({
      value,
      comparisonValue,
      comparisonLabel: compLabel,
      title: title(key),
      color: style(key).color,
      isDashed: style(key).isDashed,
      ...extra
    })

    return [
      metric(
        "chargeback",
        isRate ? blocks.chargeback_rate : blocks.chargeback_count,
        isRate ? compBlocks?.chargeback_rate : compBlocks?.chargeback_count
      ),
      metric(
        "fraud",
        isRate ? blocks.fraud_rate : blocks.fraud_count,
        isRate ? compBlocks?.fraud_rate : compBlocks?.fraud_count
      ),
      metric(
        "three_ds",
        isRegionResolved
          ? isRate
            ? blocks.three_ds_usage_rate
            : blocks.three_ds_payment_count
          : null,
        isRate ? compBlocks?.three_ds_usage_rate : compBlocks?.three_ds_payment_count,
        // A rise in 3DS usage is an improvement, unlike every other metric on the page — the EFM
        // condition is met when usage is at or below the limit, so a fall raises the risk.
        { marker: HIGHER_IS_BETTER.marker, tooltip: HIGHER_IS_BETTER.tooltip }
      )
    ]
  }, [mcMainChart, dataType, isStripeConnected])

  const ecmRate = mcMainChart?.thresholds?.ecm?.rate
  const efmRate = mcMainChart?.thresholds?.efm?.rate
  const infoLineText = `For the best health of your business, keep your Chargeback Rate below the ${
    ecmRate != null ? `${ecmRate}%` : "-"
  } ECM line and your Fraud Rate below the ${
    efmRate != null ? `${Number(efmRate).toFixed(2)}%` : "-"
  } EFM line.`

  return (
    <div
      className={cls("health-monitor-main-chart-block", {
        "is-mobile": isMobile
      })}
    >
      <Spin spinning={mcMainChartFetching}>
        {/* The title and the chart controls share one row, with the controls flush right; the
            metrics row sits below them. */}
        <Flex
          gap={20}
          align="center"
          justify="space-between"
          wrap="wrap"
          className="chart-head-row"
        >
          <SectionHeader
            title="Mastercard Monitoring Programs (ECP & EFM)"
            image={Images.mastercard_3d}
          />
          <Flex gap={8} className="main-chart-legend__controls">
            <ButtonComponent
              size={"large"}
              icon={<Icon type={"question_help"} />}
              onClick={() => setIsFAQOpen(true)}
            >
              What is ECP &amp; EFM?
            </ButtonComponent>
            <Space.Compact size={"large"}>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "rate"
                })}
                style={{
                  borderRadius: "12px 0 0 12px",
                  minWidth: 66
                }}
                disabled={isRestricted}
                onClick={() => setDataType("rate")}
              >
                {t("Rate")}
              </ButtonComponent>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "count"
                })}
                style={{
                  minWidth: 66
                }}
                disabled={isRestricted}
                onClick={() => setDataType("count")}
              >
                {t("Count")}
              </ButtonComponent>
            </Space.Compact>
          </Flex>
        </Flex>
        <div className="main-chart-legend">
          <MetricsRow
            metrics={metricBlocks}
            dataType={dataType}
            isRestricted={isRestricted}
            isComparisonVisible={isComparisonVisible}
          />
        </div>
        {isRestricted ? (
          <PermissionPlaceholder minHeight={300} />
        ) : (
          <div
            className={cls("mc-stripe-gated-content", {
              "is-stripe-disconnected": !isStripeConnected
            })}
            style={!isStripeConnected ? { minHeight: 340 } : undefined}
          >
            {isStripeConnected && <MCChart data={mcMainChart} dataType={dataType} />}
            <InfoLine text={infoLineText} variant="neutral" style={{ marginTop: "16px" }} />
            {!isStripeConnected && <ConnectStripePlaceholder />}
          </div>
        )}
      </Spin>
      <FAQModal
        open={isFAQOpen}
        close={() => setIsFAQOpen(false)}
        data={mcFAQ}
        title={"Learn more about Mastercard's ECP & EFM monitoring programs"}
      />
    </div>
  )
}

export default MCChartBlock
